import * as THREE from 'three'
import type { TipoAlvoImpacto } from '../../dominio/didatico/alvosImpacto'
import { pesoTransversal, vinco } from '../../dominio/didatico/campoDeformacao'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { criarCaixaArredondada, geometriaBase, type TransformacaoRepouso } from './malhaDeformavel'

/**
 * Geometria da dianteira deformável.
 *
 * O caminho principal é o campo de deformação (`campoDeformacao.ts`) aplicado
 * pelo `useGeometriaDeformada` sobre as bases daqui: capô e para-choque
 * subdivididos o bastante para dobrar, compartilhados entre veículos do mesmo
 * perfil e nunca alterados (quem deforma clona).
 */

export const Y_BASE_FASCIA = 0.26
export const PROFUNDIDADE_FASCIA = 0.16

export function dimensoesCapo(perfil: PerfilVeiculo) {
  return {
    largura: perfil.largura - 0.1,
    altura: 0.05,
    comprimento: perfil.frenteZ - perfil.colunaA.zBase - 0.12,
  }
}

/** Capô: 18 × 20 divisões — o suficiente para três vincos legíveis. */
export function baseCapo(perfil: PerfilVeiculo): THREE.BufferGeometry {
  const { largura, altura, comprimento } = dimensoesCapo(perfil)
  return geometriaBase(`capo:${perfil.id}`, () =>
    criarCaixaArredondada(largura, altura, comprimento, [18, 1, 20], 0.012),
  )
}

/** Pose do capô fechado no veículo (a mesma das duas versões da Carroceria). */
export const repousoCapo = (perfil: PerfilVeiculo): TransformacaoRepouso => ({
  posicao: [0, perfil.capoY, (perfil.colunaA.zBase + perfil.frenteZ) / 2 + 0.05],
  rotacao: [-0.035, 0, 0],
})

/** Para-choque (dianteiro ou traseiro) com quinas arredondadas e faces subdivididas. */
export function baseParaChoque(perfil: PerfilVeiculo, yTopo: number): THREE.BufferGeometry {
  const altura = yTopo - Y_BASE_FASCIA
  const largura = perfil.largura - 0.04
  return geometriaBase(`paraChoque:${perfil.id}:${altura.toFixed(3)}`, () =>
    criarCaixaArredondada(largura, altura, PROFUNDIDADE_FASCIA, [28, 5, 3], 0.075),
  )
}

// ---------------------------------------------------------------------------
// Funções autônomas (sem perfil): mantidas pela API existente e pelos testes.
// Usam as MESMAS funções de perfil transversal e vinco do campo.
// ---------------------------------------------------------------------------

/**
 * Cria a geometria do para-choque dianteiro, deformada proporcionalmente à intrusão
 * e ao perfil do obstáculo (poste pontual, muro amplo ou veículo).
 *
 * Não substitui a malha original no cache de perfil: gera uma geometria dedicada
 * por instância, determinística e livre de vazamento de estado.
 */
export function criarGeometriaParachoqueFrontal(
  largura: number,
  altura: number,
  profundidade: number,
  intrusaoM: number,
  tipoAlvo: TipoAlvoImpacto = 'poste',
): THREE.BoxGeometry {
  const geometria = new THREE.BoxGeometry(largura, altura, profundidade, 24, 4, 6)
  if (intrusaoM <= 0.001) return geometria

  const posicoes = geometria.attributes.position
  const meiaLargura = Math.max(largura / 2, 1e-5)
  const meioZ = profundidade / 2
  const meiaContato = tipoAlvo === 'poste' ? 0.225 : tipoAlvo === 'muro' ? meiaLargura : meiaLargura * 0.7

  for (let i = 0; i < posicoes.count; i += 1) {
    const x = posicoes.getX(i)
    const y = posicoes.getY(i)
    const z = posicoes.getZ(i)
    const fator = pesoTransversal(tipoAlvo, x, meiaContato)
    // a face frontal recua inteira; a de trás, menos: a peça comprime
    const fracaoZ = (z + meioZ) / (2 * meioZ)
    const recuo = intrusaoM * fator * (0.55 + 0.45 * fracaoZ)
    // poste: o que está ao lado converge para ele; muro: os cantos se abrem
    const lateral =
      tipoAlvo === 'poste'
        ? -Math.sign(x) * intrusaoM * 0.2 * Math.max(0, 1 - Math.abs(x) / (meiaContato * 3.5))
        : Math.sign(x) * intrusaoM * 0.08 * fator * (x / meiaLargura) ** 2
    posicoes.setXYZ(i, x + lateral, y - intrusaoM * 0.06 * fator * fracaoZ, z - recuo)
  }

  posicoes.needsUpdate = true
  geometria.computeVertexNormals()
  geometria.computeBoundingSphere()
  return geometria
}

/**
 * Cria a geometria do capô dianteiro deformado em sanfona: encurta na borda de
 * ataque e dobra em três vincos (sobe, desce, sobe), com as linhas de dobra
 * apontando para o poste quando o alvo é estreito.
 */
export function criarGeometriaCapoFrontal(
  largura: number,
  altura: number,
  comprimento: number,
  intrusaoM: number,
  tipoAlvo: TipoAlvoImpacto = 'poste',
): THREE.BoxGeometry {
  const geometria = new THREE.BoxGeometry(largura, altura, comprimento, 16, 1, 20)
  if (intrusaoM <= 0.001) return geometria

  const posicoes = geometria.attributes.position
  const meiaLargura = Math.max(largura / 2, 1e-5)
  const meioComprimento = Math.max(comprimento / 2, 1e-5)
  const meiaContato = tipoAlvo === 'poste' ? 0.225 : tipoAlvo === 'muro' ? meiaLargura : meiaLargura * 0.7
  const zonaEsmagada = Math.min(comprimento, intrusaoM * 2.3 + 0.25)

  for (let i = 0; i < posicoes.count; i += 1) {
    const x = posicoes.getX(i)
    const y = posicoes.getY(i)
    const z = posicoes.getZ(i)
    // s: 1 na borda de ataque, 0 no fim da zona esmagada
    const s = Math.max(0, Math.min(1, 1 - (meioComprimento - z) / zonaEsmagada))
    const w = pesoTransversal(tipoAlvo, x, meiaContato)
    const desvio = tipoAlvo === 'poste' ? 0.32 * Math.min(1, Math.abs(x) / (meiaContato * 3)) : 0.05 * (x / meiaLargura) ** 2
    const elevacao =
      vinco(s - (0.82 + desvio), 0.13) - 0.3 * vinco(s - (0.55 + desvio), 0.13) + 0.75 * vinco(s - (0.28 + desvio), 0.13)
    const wElev = Math.min(1.2, 0.35 + 0.65 * w)
    posicoes.setXYZ(
      i,
      x,
      y + intrusaoM * (0.42 * elevacao * wElev + 0.1 * w * Math.sin(Math.PI * s)),
      z - intrusaoM * w * Math.pow(s, 1.1),
    )
  }

  posicoes.needsUpdate = true
  geometria.computeVertexNormals()
  geometria.computeBoundingSphere()
  return geometria
}
